import { ref, computed } from "vue";
import { onBeforeRouteLeave, } from "vue-router";
import { useI18n } from "vue-i18n";
import { useDirtyState as useDirtyStateCore } from "@eudonet/opal";
/**
 * Application-level dirty-state composable with Vue Router navigation protection.
 *
 * Delegates core dirty tracking to OPAL's `useDirtyState` and adds:
 * - `onBeforeRouteLeave` guard that blocks navigation when dirty
 * - `proceedNavigation` / `cancelNavigation` for modal-based confirmation
 *
 * The public API is identical to the previous standalone implementation
 * so existing consumers require zero changes.
 */
export function useDirtyState(options) {
    const { data, onNavigationBlocked, enableBeforeUnload = true } = options;
    const { t } = useI18n();
    // Delegate core dirty tracking to OPAL
    const core = useDirtyStateCore({
        data,
        enableBeforeUnload,
        beforeUnloadMessage: () => t("unsavedChanges.beforeUnloadMessage"),
    });
    // ── Router-specific navigation blocking ──────────────────────────
    const pendingNavigation = ref(null);
    const hasPendingNavigation = computed(() => pendingNavigation.value !== null);
    function proceedNavigation() {
        if (pendingNavigation.value) {
            const { next } = pendingNavigation.value;
            pendingNavigation.value = null;
            core.setDirty(false);
            core.markAsSaved();
            next();
        }
    }
    function cancelNavigation() {
        if (pendingNavigation.value) {
            const { next } = pendingNavigation.value;
            pendingNavigation.value = null;
            next(false);
        }
    }
    onBeforeRouteLeave((_to, _from, next) => {
        if (core.isDirty.value && onNavigationBlocked) {
            pendingNavigation.value = { to: _to, next };
            onNavigationBlocked(proceedNavigation, cancelNavigation);
        }
        else {
            next();
        }
    });
    return {
        isDirty: core.isDirty,
        markAsSaved: core.markAsSaved,
        resetToOriginal: core.resetToOriginal,
        getOriginalData: core.getOriginalData,
        setDirty: core.setDirty,
        proceedNavigation,
        cancelNavigation,
        hasPendingNavigation: hasPendingNavigation,
    };
}
